import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = ["super_admin", "astrologer", "receptionist", "accountant", "marketing_executive"];

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: "", name: "", role: "receptionist", password: "" });
  const [dlgOpen, setDlgOpen] = useState(false);

  const isAdmin = user?.role === "super_admin";

  useEffect(() => {
    api.get("/settings").then(r => setSettings(r.data));
    if (isAdmin) api.get("/users").then(r => setUsers(r.data));
  }, [isAdmin]);

  const saveSettings = async () => { await api.put("/settings", settings); toast.success("Settings saved"); };

  const addUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) return toast.error("All fields required");
    try {
      const { data } = await api.post("/users", newUser);
      setUsers([...users, data]);
      toast.success("User added");
      setDlgOpen(false);
      setNewUser({ email: "", name: "", role: "receptionist", password: "" });
    } catch (e) { toast.error("Add failed"); }
  };

  const removeUser = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    setUsers(users.filter(u => u.id !== id));
  };

  if (!settings) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Configuration</div>
        <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Settings</h1>
      </div>

      <Tabs defaultValue="company">
        <TabsList className="rounded-full">
          <TabsTrigger value="company" className="rounded-full" data-testid="tab-company">Company</TabsTrigger>
          <TabsTrigger value="charges" className="rounded-full" data-testid="tab-charges">Consultation Charges</TabsTrigger>
          {isAdmin && <TabsTrigger value="users" className="rounded-full" data-testid="tab-users">User Management</TabsTrigger>}
        </TabsList>

        <TabsContent value="company" className="mt-6">
          <Card className="p-6 rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
            {[["Company name","company_name"],["Tagline","tagline"],["Address","address"],["Phone","phone"],["Email","email"],["Website","website"],["GST Number","gst_number"],["Currency","currency"]].map(([lbl, k]) => (
              <div key={k}>
                <Label className="text-xs uppercase tracking-widest text-slate-500">{lbl}</Label>
                <Input value={settings[k] || ""} onChange={e=>setSettings({...settings,[k]:e.target.value})} className="mt-1" />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500">GST %</Label>
              <Input type="number" value={settings.gst_percent} onChange={e=>setSettings({...settings,gst_percent:parseFloat(e.target.value)})} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Button onClick={saveSettings} className="bg-[#1E3A8A] btn-pill" data-testid="save-settings"><Save className="w-4 h-4 mr-1.5" /> Save</Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="charges" className="mt-6">
          <Card className="p-6 rounded-2xl grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(settings.consultation_charges || {}).map(([type, price]) => (
              <div key={type}>
                <Label className="text-xs uppercase tracking-widest text-slate-500">{type}</Label>
                <Input type="number" value={price} onChange={e=>setSettings({...settings,consultation_charges:{...settings.consultation_charges,[type]:parseFloat(e.target.value)}})} className="mt-1" />
              </div>
            ))}
            <div className="md:col-span-2">
              <Button onClick={saveSettings} className="bg-[#1E3A8A] btn-pill">Save charges</Button>
            </div>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="users" className="mt-6 space-y-3">
            <div className="flex justify-end">
              <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
                <DialogTrigger asChild><Button className="bg-[#1E3A8A] btn-pill" data-testid="new-user-btn"><Plus className="w-4 h-4 mr-1.5" /> Add user</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display text-2xl">Add team member</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="col-span-2"><Label>Name</Label><Input value={newUser.name} onChange={e=>setNewUser({...newUser,name:e.target.value})} /></div>
                    <div className="col-span-2"><Label>Email</Label><Input type="email" value={newUser.email} onChange={e=>setNewUser({...newUser,email:e.target.value})} /></div>
                    <div><Label>Role</Label>
                      <Select value={newUser.role} onValueChange={v=>setNewUser({...newUser,role:v})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_',' ')}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Password</Label><Input type="password" value={newUser.password} onChange={e=>setNewUser({...newUser,password:e.target.value})} /></div>
                  </div>
                  <div className="flex justify-end mt-4"><Button onClick={addUser} className="bg-[#1E3A8A] btn-pill" data-testid="submit-user">Add</Button></div>
                </DialogContent>
              </Dialog>
            </div>
            {users.map(u => (
              <Card key={u.id} className="p-4 rounded-2xl flex items-center justify-between" data-testid={`user-${u.id}`}>
                <div>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-sm text-slate-500">{u.email}</div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant="secondary">{u.role?.replace('_',' ')}</Badge>
                  <Button size="icon" variant="ghost" onClick={()=>removeUser(u.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </Card>
            ))}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
